import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import Box from '../Box';
import Button from '../Button';
import Group from '../Group';
import StackView from '../StackView';
import { range } from '../utils';
import { parseColor } from '../system/utils';
import { useWindowWidth } from '@react-hook/window-size';
import { useThemeValue, useThemeProps } from '../system';

function getVisiblePages(currentPage, totalPages, visiblePages) {
  var start = Math.max(1, Math.min(totalPages - visiblePages + 1, Math.floor(currentPage - visiblePages / 2 + 1)));
  var stop = Math.min(totalPages, start + visiblePages - 1);
  var primaryWindow = range(start, stop + 1);
  var showFirstPage = start > 1;
  var showLastPage = stop < totalPages;

  if (showFirstPage) {
    primaryWindow.shift();
    primaryWindow.shift();
  }

  if (showLastPage) {
    primaryWindow.pop();
    primaryWindow.pop();
  }

  var first = showFirstPage ? [1, '...'] : [];
  var last = showLastPage ? ['...', totalPages] : [];
  return first.concat(primaryWindow).concat(last);
}

var NavButton = function NavButton(_ref) {
  var disabled = _ref.disabled,
      iconName = _ref.iconName,
      props = _objectWithoutPropertiesLoose(_ref, ["disabled", "iconName"]);

  return /*#__PURE__*/React.createElement(Button, _extends({
    backgroundColor: disabled ? '#f7f7f7' : '#f2f2f2',
    disabled: disabled,
    icon: {
      color: disabled ? '#cfcfcf' : undefined,
      name: iconName,
      size: 'xs'
    },
    opacity: 1,
    shrink: 0,
    square: true,
    variant: "fill"
  }, props));
};

export default function Pagination(_ref2) {
  var currentPage = _ref2.currentPage,
      _ref2$onPageChange = _ref2.onPageChange,
      onPageChange = _ref2$onPageChange === void 0 ? function () {
    return null;
  } : _ref2$onPageChange,
      visiblePages = _ref2.visiblePages,
      _ref2$totalPages = _ref2.totalPages,
      totalPages = _ref2$totalPages === void 0 ? 0 : _ref2$totalPages,
      restProps = _objectWithoutPropertiesLoose(_ref2, ["currentPage", "onPageChange", "visiblePages", "totalPages"]);

  var _useThemeValue = useThemeValue('breakpoints'),
      xsBreakpoint = _useThemeValue.xs;

  var _useThemeProps = useThemeProps('pagination', _objectSpread({}, restProps)),
      _useThemeProps$active = _useThemeProps.activeColor,
      activeColor = _useThemeProps$active === void 0 ? '#5b8bf7' : _useThemeProps$active,
      navButtonProps = _useThemeProps.navButtonProps,
      pageLinkProps = _useThemeProps.pageLinkProps,
      activePageLinkProps = _useThemeProps.activePageLinkProps,
      themeProps = _objectWithoutPropertiesLoose(_useThemeProps, ["activeColor", "navButtonProps", "pageLinkProps", "activePageLinkProps"]);

  var currentWidth = useWindowWidth();

  function renderGap(key) {
    return /*#__PURE__*/React.createElement(Box, {
      key: key,
      userSelect: "none",
      cursor: "default",
      children: "\u2026"
    });
  }

  function renderPageLink(number) {
    var isCurrentPage = currentPage === number;
    var activeBackgroundColor = parseColor({
      base: activeColor,
      alpha: 0.1
    });
    var outlineStyle = {
      border: "1px solid " + activeColor,
      color: isCurrentPage ? '#fff' : activeColor
    };
    return /*#__PURE__*/React.createElement(Button, _extends({
      active: _objectSpread({
        backgroundColor: isCurrentPage ? activeColor : activeBackgroundColor
      }, outlineStyle),
      key: number,
      fontWeight: 500,
      hover: _objectSpread({
        backgroundColor: isCurrentPage ? activeColor : '#fff'
      }, outlineStyle),
      onClick: onPageChange.bind(null, number),
      shrink: 0,
      square: true,
      title: number,
      variant: isCurrentPage ? 'fill' : 'naked'
    }, pageLinkProps, isCurrentPage && _objectSpread({
      backgroundColor: activeColor,
      color: '#fff'
    }, activePageLinkProps)));
  }

  function renderPageLinks() {
    var visiblePagesToShow = currentWidth <= xsBreakpoint ? visiblePages || 5 : visiblePages || 7;
    var pages = getVisiblePages(currentPage, totalPages, visiblePagesToShow);
    return pages.map(function (page, index) {
      return page === '...' ? renderGap(page + index) : renderPageLink(page);
    });
  }

  if (totalPages <= 1) {
    return null;
  } else {
    return /*#__PURE__*/React.createElement(StackView, _extends({
      alignment: "center",
      axis: "horizontal",
      distribution: "center",
      overflow: "auto",
      paddingVertical: 1,
      shrink: 0,
      spacing: 0.5,
      width: "100%"
    }, themeProps), /*#__PURE__*/React.createElement(Group, {
      spacing: 0.5
    }, /*#__PURE__*/React.createElement(NavButton, _extends({
      disabled: currentPage === 1,
      iconName: "general.leftChevron",
      onClick: onPageChange.bind(null, currentPage - 1),
      tooltip: {
        title: 'Previous Page'
      }
    }, navButtonProps)), /*#__PURE__*/React.createElement(NavButton, _extends({
      disabled: currentPage === totalPages,
      iconName: "general.rightChevron",
      onClick: onPageChange.bind(null, currentPage + 1),
      tooltip: {
        title: 'Next Page'
      }
    }, navButtonProps))), renderPageLinks());
  }
}